import React, { useState, useEffect } from 'react';
import { Copy, CheckCircle } from 'lucide-react';
import { getUID, storage, getSocket, encode, decode, C } from '@/utils';
import { useToast } from '@/hooks/useToast';
import styles from './AccountInfoSection.module.css';

/**
 * AccountInfoSection Component
 * Displays user account information with email verification
 */
const AccountInfoSection = () => {
  const { showSuccess, showError } = useToast();
  const [email, setEmail] = useState('');
  const [isEmailVerified, setIsEmailVerified] = useState(false);
  const [userId, setUserId] = useState('');
  const [referredBy, setReferredBy] = useState('N/A');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const uid = getUID();
    if (uid) {
      setUserId(uid);
    }

    // Get email from storage
    const storedEmail = storage.getKey('email') || localStorage.getItem('email') || '';
    setEmail(storedEmail);

    // Check email verification status
    const verified = storage.getKey('email_verified') || localStorage.getItem('email_verified');
    setIsEmailVerified(verified === 'true' || verified === true);

    // Request user info from socket
    const currentSocket = getSocket();
    if (uid && currentSocket) {
      currentSocket.emit(C.USER_INFO, encode({
        id: uid,
        coin: storage.getKey('coin') || '',
        rate: null,
        game: 'all',
        first: true,
      }));
    }

    setIsLoading(false);
  }, []);

  const handleCopyUserId = () => {
    navigator.clipboard.writeText(userId);
    showSuccess('User ID copied to clipboard!');
  };

  const handleVerifyEmail = () => {
    // TODO: Implement email verification
    showSuccess('Verification email sent!');
  };

  if (isLoading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.loader}>
          <span className={styles.loaderText}>loading</span>
          <span className={styles.load}></span>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <h3 className={styles.sectionTitle}>Account Information</h3>

      {/* Communications Section */}
      <div className={styles.section}>
        <div className={styles.settingRow}>
          <div className={styles.settingInfo}>
            <div className={styles.settingTitle}>Email Address</div>
            <div className={styles.inputWrapper}>
              <input
                type="email"
                className={styles.input}
                value={email}
                readOnly
                placeholder="No email set"
              />
              <button
                className={styles.verifyButton}
                onClick={handleVerifyEmail}
                disabled={!email || isEmailVerified}
              >
                {isEmailVerified ? 'Verified' : 'Verify'}
              </button>
            </div>
            {isEmailVerified && (
              <div className={styles.verifiedBadge}>
                <CheckCircle size={16} />
                <span>Email has been verified</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Referrer Section */}
      <div className={styles.section}>
        <div className={styles.settingRow}>
          <div className={styles.settingInfo}>
            <div className={styles.settingTitle}>Referred By</div>
            <input
              type="text"
              className={styles.input}
              value={referredBy}
              readOnly
              disabled
            />
          </div>
        </div>
      </div>

      {/* User ID Section */}
      <div className={styles.section}>
        <div className={styles.settingRow}>
          <div className={styles.settingInfo}>
            <div className={styles.settingTitle}>User ID</div>
            <div className={styles.userIdWrapper}>
              <span className={styles.userId}>{userId || 'N/A'}</span>
              <button
                className={styles.copyButton}
                onClick={handleCopyUserId}
                aria-label="Copy User ID"
              >
                <Copy size={16} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccountInfoSection;
